// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file PowerSearchValueEditor.tsx
 * @input OperatorValue, FilterValue, onChange callback
 * @output Renders the appropriate editor for a filter value type
 * @position Sub-component; consumed by PowerSearchEditPopover
 *
 * SYNC: When modified, update:
 * - /packages/core/src/PowerSearch/index.ts
 */
import React, { useCallback, useMemo } from 'react';
// Lazy import to avoid circular deps — these are all from the same package
import { TextInput } from "../TextInput/index.js";
import { NumberInput } from "../NumberInput/index.js";
import { DateInput } from "../DateInput/index.js";
import { TimeInput } from "../TimeInput/index.js";
import { Selector } from "../Selector/index.js";
import { Tokenizer } from "../Tokenizer/index.js";
import { Typeahead } from "../Typeahead/index.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Helpers
// =============================================================================

function createStaticSource(items) {
  return {
    search(query) {
      const lower = query.toLowerCase();
      return items.filter(item => item.label.toLowerCase().includes(lower));
    },
    bootstrap() {
      return items;
    }
  };
}
function enumItemsToSearchableItems(values) {
  return values.map(item => ({
    id: item.value,
    label: item.label
  }));
}

// =============================================================================
// Individual Editors
// =============================================================================

function StringEditor({
  operatorValue,
  filterValue,
  onChange,
  onEnter: _onEnter
}) {
  const t = useTranslator();
  const currentValue = filterValue?.type === 'string' ? filterValue.value : '';

  // When a searchSource is provided, render a typeahead instead of a plain
  // text input so users get suggestions (#1103).
  if (operatorValue.searchSource) {
    const selectedItem = currentValue ? {
      id: currentValue,
      label: currentValue
    } : null;
    return /*#__PURE__*/_jsx(Typeahead, {
      label: t('@astryx.powersearch.valueEditor.value'),
      isLabelHidden: true,
      searchSource: operatorValue.searchSource,
      value: selectedItem,
      onChange: item => {
        if (item) {
          onChange({
            type: 'string',
            value: item.label
          }, true);
        } else {
          onChange({
            type: 'string',
            value: ''
          });
        }
      },
      placeholder: t('@astryx.powersearch.valueEditor.searchPlaceholder'),
      debounceMs: 150
    });
  }
  return /*#__PURE__*/_jsx(TextInput, {
    label: t('@astryx.powersearch.valueEditor.value'),
    isLabelHidden: true,
    value: currentValue,
    placeholder: t('@astryx.powersearch.valueEditor.enterValuePlaceholder'),
    onChange: value => {
      onChange({
        type: 'string',
        value
      });
    }
  });
}
function StringListEditor({
  operatorValue,
  filterValue,
  onChange
}) {
  const t = useTranslator();
  const currentValue = useMemo(() => {
    if (filterValue?.type !== 'string_list') {
      return [];
    }
    return filterValue.value.map(v => ({
      id: v,
      label: v
    }));
  }, [filterValue]);
  const source = useMemo(() => {
    if (operatorValue.searchSource) {
      return operatorValue.searchSource;
    }
    // Free-text: accept anything typed
    return {
      search: () => [],
      bootstrap: () => []
    };
  }, [operatorValue.searchSource]);

  // Enable creatable mode when no searchSource is provided (free-text tags)
  // or when isArbitraryStringAllowed is explicitly set (#1107).
  const hasCreate = operatorValue.isArbitraryStringAllowed || !operatorValue.searchSource;
  return /*#__PURE__*/_jsx(Tokenizer, {
    label: t('@astryx.powersearch.valueEditor.values'),
    isLabelHidden: true,
    searchSource: source,
    value: currentValue,
    onChange: items => {
      onChange({
        type: 'string_list',
        value: items.map(item => item.label)
      });
    },
    placeholder: t('@astryx.powersearch.valueEditor.addValuesPlaceholder'),
    debounceMs: operatorValue.searchSource ? 150 : 0,
    hasCreate: hasCreate
  });
}
function IntegerEditor({
  operatorValue,
  filterValue,
  onChange
}) {
  const t = useTranslator();
  const currentValue = filterValue?.type === 'integer' ? filterValue.value : undefined;
  return /*#__PURE__*/_jsx(NumberInput, {
    label: t('@astryx.powersearch.valueEditor.value'),
    isLabelHidden: true,
    value: currentValue ?? null,
    onChange: value => {
      onChange({
        type: 'integer',
        value
      });
    },
    min: operatorValue.minValue,
    max: operatorValue.maxValue,
    units: operatorValue.units,
    isIntegerOnly: true,
    placeholder: t('@astryx.powersearch.valueEditor.enterNumberPlaceholder')
  });
}
function FloatEditor({
  operatorValue,
  filterValue,
  onChange
}) {
  const t = useTranslator();
  const currentValue = filterValue?.type === 'float' ? filterValue.value : undefined;
  return /*#__PURE__*/_jsx(NumberInput, {
    label: t('@astryx.powersearch.valueEditor.value'),
    isLabelHidden: true,
    value: currentValue ?? null,
    onChange: value => {
      onChange({
        type: 'float',
        value
      });
    },
    min: operatorValue.minValue,
    max: operatorValue.maxValue,
    units: operatorValue.units,
    placeholder: t('@astryx.powersearch.valueEditor.enterNumberPlaceholder')
  });
}
function TimeEditor({
  operatorValue,
  filterValue,
  onChange
}) {
  const t = useTranslator();
  const currentValue = filterValue?.type === 'time' ? filterValue.value : undefined;
  return /*#__PURE__*/_jsx(TimeInput, {
    label: t('@astryx.powersearch.valueEditor.time'),
    isLabelHidden: true,
    value: currentValue,
    onChange: value => {
      if (value != null) {
        onChange({
          type: 'time',
          value
        });
      }
    },
    min: operatorValue.minValue,
    max: operatorValue.maxValue
  });
}
function DateAbsoluteEditor({
  filterValue,
  onChange
}) {
  const t = useTranslator();
  // Convert unixSeconds to ISO date string for the date input
  const currentValue = useMemo(() => {
    if (filterValue?.type !== 'date_absolute') {
      return undefined;
    }
    const date = new Date(filterValue.unixSeconds * 1000);
    return date.toISOString().split('T')[0];
  }, [filterValue]);
  return /*#__PURE__*/_jsx(DateInput, {
    label: t('@astryx.powersearch.valueEditor.date'),
    isLabelHidden: true,
    value: currentValue,
    onChange: value => {
      if (value != null) {
        const unixSeconds = Math.floor(new Date(value).getTime() / 1000);
        onChange({
          type: 'date_absolute',
          unixSeconds
        });
      }
    }
  });
}
function DateRelativeEditor({
  operatorValue,
  filterValue,
  onChange
}) {
  const t = useTranslator();
  const currentValue = filterValue?.type === 'date_relative' ? filterValue.value : undefined;
  const options = useMemo(() => {
    const result = [];
    const units = [{
      unit: 'day',
      plural: 'days'
    }, {
      unit: 'week',
      plural: 'weeks'
    }, {
      unit: 'month',
      plural: 'months'
    }];
    for (const {
      unit,
      plural
    } of units) {
      const amounts = unit === 'day' ? [1, 3, 7, 14, 30] : unit === 'week' ? [1, 2, 4] : [1, 3, 6, 12];
      for (const amount of amounts) {
        if (operatorValue.isPastAllowed !== false) {
          result.push({
            value: `${amount}${unit[0]}_ago`,
            label: `${amount} ${amount === 1 ? unit : plural} ago`
          });
        }
        if (operatorValue.isFutureAllowed !== false) {
          result.push({
            value: `${amount}${unit[0]}_from_now`,
            label: `${amount} ${amount === 1 ? unit : plural} from now`
          });
        }
      }
    }
    return result;
  }, [operatorValue.isPastAllowed, operatorValue.isFutureAllowed]);
  return /*#__PURE__*/_jsx(Selector, {
    label: t('@astryx.powersearch.valueEditor.relativeDate'),
    isLabelHidden: true,
    options: options,
    value: currentValue,
    onChange: value => {
      onChange({
        type: 'date_relative',
        value
      }, true);
    }
  });
}
function DateRangeEditor({
  filterValue,
  onChange
}) {
  const t = useTranslator();
  const startValue = useMemo(() => {
    if (filterValue?.type !== 'date_range') {
      return undefined;
    }
    const part = filterValue.value.start;
    if (part.type === 'ABSOLUTE') {
      return new Date(part.unixSeconds * 1000).toISOString().split('T')[0];
    }
    return undefined;
  }, [filterValue]);
  const endValue = useMemo(() => {
    if (filterValue?.type !== 'date_range') {
      return undefined;
    }
    const part = filterValue.value.end;
    if (part.type === 'ABSOLUTE') {
      return new Date(part.unixSeconds * 1000).toISOString().split('T')[0];
    }
    return undefined;
  }, [filterValue]);
  const handleStartChange = useCallback(value => {
    const startUnix = value ? Math.floor(new Date(value).getTime() / 1000) : 0;
    const existingEnd = filterValue?.type === 'date_range' ? filterValue.value.end : {
      type: 'NOW'
    };
    onChange({
      type: 'date_range',
      value: {
        start: {
          type: 'ABSOLUTE',
          unixSeconds: startUnix
        },
        end: existingEnd
      }
    });
  }, [filterValue, onChange]);
  const handleEndChange = useCallback(value => {
    const endUnix = value ? Math.floor(new Date(value).getTime() / 1000) : 0;
    const existingStart = filterValue?.type === 'date_range' ? filterValue.value.start : {
      type: 'NOW'
    };
    onChange({
      type: 'date_range',
      value: {
        start: existingStart,
        end: {
          type: 'ABSOLUTE',
          unixSeconds: endUnix
        }
      }
    });
  }, [filterValue, onChange]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(DateInput, {
      label: t('@astryx.powersearch.valueEditor.startDate'),
      isLabelHidden: true,
      value: startValue,
      onChange: handleStartChange
    }), /*#__PURE__*/_jsx(DateInput, {
      label: t('@astryx.powersearch.valueEditor.endDate'),
      isLabelHidden: true,
      value: endValue,
      onChange: handleEndChange
    })]
  });
}
function EnumEditor({
  operatorValue,
  filterValue,
  onChange
}) {
  const t = useTranslator();
  const currentValue = filterValue?.type === 'enum' ? filterValue.value : undefined;
  const options = useMemo(() => operatorValue.values.map(item => ({
    value: item.value,
    label: item.label
  })), [operatorValue.values]);
  return /*#__PURE__*/_jsx(Selector, {
    label: t('@astryx.powersearch.valueEditor.value'),
    isLabelHidden: true,
    options: options,
    value: currentValue,
    onChange: value => {
      onChange({
        type: 'enum',
        value
      }, true);
    }
  });
}
function EnumListEditor({
  operatorValue,
  filterValue,
  onChange
}) {
  const t = useTranslator();
  const items = useMemo(() => enumItemsToSearchableItems(operatorValue.values), [operatorValue.values]);
  const source = useMemo(() => createStaticSource(items), [items]);
  const currentValue = useMemo(() => {
    if (filterValue?.type !== 'enum_list') {
      return [];
    }
    return filterValue.value.map(v => {
      const item = operatorValue.values.find(e => e.value === v);
      return {
        id: v,
        label: item?.label ?? v
      };
    });
  }, [filterValue, operatorValue.values]);
  return /*#__PURE__*/_jsx(Tokenizer, {
    label: t('@astryx.powersearch.valueEditor.values'),
    isLabelHidden: true,
    searchSource: source,
    value: currentValue,
    onChange: selectedItems => {
      onChange({
        type: 'enum_list',
        value: selectedItems.map(item => item.id)
      });
    },
    placeholder: t('@astryx.powersearch.valueEditor.selectValuesPlaceholder'),
    hasEntriesOnFocus: true,
    debounceMs: 0
  });
}
function EntityListEditor({
  operatorValue,
  filterValue,
  onChange
}) {
  const t = useTranslator();
  const source = useMemo(() => {
    if (operatorValue.searchSource) {
      return operatorValue.searchSource;
    }
    return {
      search: () => [],
      bootstrap: () => []
    };
  }, [operatorValue.searchSource]);

  // Preserve photo in auxiliaryData so it round-trips through the tokenizer (#1106).
  const currentValue = useMemo(() => {
    if (filterValue?.type !== 'entity_list') {
      return [];
    }
    return filterValue.value.map(entity => ({
      id: entity.id,
      label: entity.label,
      auxiliaryData: entity.photo ? {
        photo: entity.photo
      } : undefined
    }));
  }, [filterValue]);
  return /*#__PURE__*/_jsx(Tokenizer, {
    label: t('@astryx.powersearch.valueEditor.entities'),
    isLabelHidden: true,
    searchSource: source,
    value: currentValue,
    onChange: items => {
      onChange({
        type: 'entity_list',
        // Round-trip photo from auxiliaryData back to PowerSearchEntity (#1106).
        value: items.map(item => {
          const aux = item.auxiliaryData;
          return {
            id: item.id,
            label: item.label,
            ...(aux?.photo ? {
              photo: aux.photo
            } : {})
          };
        })
      });
    },
    renderItem: operatorValue.renderItem,
    placeholder: t('@astryx.powersearch.valueEditor.searchPlaceholder'),
    debounceMs: operatorValue.searchSource ? 150 : 0
  });
}
function CustomEditor({
  operatorValue,
  filterValue,
  onChange,
  isDisabled
}) {
  const t = useTranslator();
  const currentValue = filterValue?.type === 'custom' ? filterValue.value : null;
  const EditorComponent = operatorValue.Editor;
  return /*#__PURE__*/_jsx(EditorComponent, {
    isDisabled: isDisabled,
    onChange: value => {
      if (value != null) {
        onChange({
          type: 'custom',
          value
        });
      }
    },
    placeholder: t('@astryx.powersearch.valueEditor.enterValuePlaceholder'),
    value: currentValue
  });
}

// =============================================================================
// Main Dispatcher
// =============================================================================

export function PowerSearchValueEditor({
  operatorValue,
  filterValue,
  onChange,
  onEnter,
  isDisabled
}) {
  switch (operatorValue.type) {
    case 'empty':
      return null;
    case 'string':
      return /*#__PURE__*/_jsx(StringEditor, {
        operatorValue: operatorValue,
        filterValue: filterValue,
        onChange: onChange,
        onEnter: onEnter
      });
    case 'string_list':
      return /*#__PURE__*/_jsx(StringListEditor, {
        operatorValue: operatorValue,
        filterValue: filterValue,
        onChange: onChange
      });
    case 'integer':
      return /*#__PURE__*/_jsx(IntegerEditor, {
        operatorValue: operatorValue,
        filterValue: filterValue,
        onChange: onChange
      });
    case 'float':
      return /*#__PURE__*/_jsx(FloatEditor, {
        operatorValue: operatorValue,
        filterValue: filterValue,
        onChange: onChange
      });
    case 'time':
      return /*#__PURE__*/_jsx(TimeEditor, {
        operatorValue: operatorValue,
        filterValue: filterValue,
        onChange: onChange
      });
    case 'date_absolute':
      return /*#__PURE__*/_jsx(DateAbsoluteEditor, {
        operatorValue: operatorValue,
        filterValue: filterValue,
        onChange: onChange
      });
    case 'date_relative':
      return /*#__PURE__*/_jsx(DateRelativeEditor, {
        operatorValue: operatorValue,
        filterValue: filterValue,
        onChange: onChange
      });
    case 'date_range':
      return /*#__PURE__*/_jsx(DateRangeEditor, {
        operatorValue: operatorValue,
        filterValue: filterValue,
        onChange: onChange
      });
    case 'enum':
      return /*#__PURE__*/_jsx(EnumEditor, {
        operatorValue: operatorValue,
        filterValue: filterValue,
        onChange: onChange
      });
    case 'enum_list':
      return /*#__PURE__*/_jsx(EnumListEditor, {
        operatorValue: operatorValue,
        filterValue: filterValue,
        onChange: onChange
      });
    case 'entity_list':
      return /*#__PURE__*/_jsx(EntityListEditor, {
        operatorValue: operatorValue,
        filterValue: filterValue,
        onChange: onChange
      });
    case 'custom':
      return /*#__PURE__*/_jsx(CustomEditor, {
        operatorValue: operatorValue,
        filterValue: filterValue,
        onChange: onChange,
        isDisabled: isDisabled
      });
    case 'nested':
      // Nested filters are complex — simplified v1 just shows a message
      return null;
    default:
      return null;
  }
}